/**
 * Initialisiert die Weekly-Navigation, markiert beim Scrollen den aktiven Bereich
 * und ermöglicht weiches Scrollen zu verknüpften Sektionen.
 */
export function initWeeklyNav(
    {
        navSelector = '.weekly__nav',
        ulSelector = 'ul',
        itemSelector = 'span[data-href^="#"]',
        activeClass = 'is-active',
        headerSelector = '.headerMain',
        mobileMaxWidth = 1249,
        leftPadding = 0,
        scrolledClass = 'is-scrolled',
        scrolledThreshold = 100,
        activateEarlyPx = -20,
        scrollGapPx = 20,
    } = {}
) {
    const nav = document.querySelector(navSelector);
    if (!nav) return;

    const ul = nav.querySelector(ulSelector);
    if (!ul) return;

    const header = document.querySelector(headerSelector);
    const itemEls = Array.from(nav.querySelectorAll(itemSelector));
    if (!itemEls.length) return;

    const mql = window.matchMedia(`(max-width: ${mobileMaxWidth}px)`);
    const isMobile = () => mql.matches;

    const items = [];
    let lastActive = null;
    let ticking = false;
    let sectionTopsDirty = true;

    const updateScrolledClass = () => {
        if (!scrolledClass) return;
        const y = window.scrollY || document.documentElement.scrollTop;
        nav.classList.toggle(scrolledClass, y >= scrolledThreshold);
    };

    const getFixedNavHeight = () => {
        if (!isMobile()) return 0;
        const pos = getComputedStyle(nav).position;
        if (pos !== 'fixed' && pos !== 'sticky') return 0;
        return nav.getBoundingClientRect().height;
    };

    const getOffsetPx = () => {
        const headerH = header ? header.getBoundingClientRect().height : 0;
        const navH = getFixedNavHeight();
        return Math.round(headerH + navH);
    };

    const canScrollHorizontally = () => isMobile() && ul.scrollWidth > ul.clientWidth;

    const scrollActiveToLeft = (el) => {
        if (!el || !canScrollHorizontally()) return;

        const elRect = el.getBoundingClientRect();
        const ulRect = ul.getBoundingClientRect();
        const current = ul.scrollLeft;

        const relativeLeft = (elRect.left - ulRect.left) + current;
        const target = Math.max(0, relativeLeft - leftPadding);

        if (Math.abs(ul.scrollLeft - target) > 1) {
            ul.scrollTo({ left: target, behavior: 'smooth' });
        }
    };

    const setActive = (el, { scrollNav = true } = {}) => {
        if (!el || el === lastActive) return;

        if (lastActive) lastActive.classList.remove(activeClass);
        el.classList.add(activeClass);
        lastActive = el;

        if (scrollNav) scrollActiveToLeft(el);
    };

    const scrollToSection = (section) => {
        const y = window.scrollY || document.documentElement.scrollTop;
        const r = section.getBoundingClientRect();

        // Ziel = SectionTop - (Header+fixedNav) - scrollGapPx
        const targetY = (r.top + y) - getOffsetPx() - scrollGapPx;

        window.scrollTo({
            top: Math.max(0, Math.round(targetY)),
            behavior: 'smooth',
        });
    };

    for (const el of itemEls) {
        const href = el.getAttribute('data-href');
        if (!href || !href.startsWith('#')) continue;

        const id = decodeURIComponent(href.slice(1));
        const section = document.getElementById(id);
        if (!section) continue;

        items.push({ el, section, top: 0, bottom: 0 });

        el.addEventListener(
            'click',
            (e) => {
                e.preventDefault?.();
                setActive(el, { scrollNav: true });
                scrollToSection(section);
                sectionTopsDirty = true;
            },
            { passive: false }
        );
    }

    if (!items.length) return;

    const recalcSections = () => {
        const y = window.scrollY || document.documentElement.scrollTop;
        for (const it of items) {
            const r = it.section.getBoundingClientRect();
            it.top = r.top + y;
            it.bottom = r.bottom + y;
        }
        sectionTopsDirty = false;
    };

    const findActiveIndex = () => {
        if (sectionTopsDirty) recalcSections();

        const y = window.scrollY || document.documentElement.scrollTop;

        const trigger = y + getOffsetPx() + 1 - activateEarlyPx;

        let active = 0;
        for (let i = 0; i < items.length; i++) {
            const { top, bottom } = items[i];
            if (trigger >= top && trigger < bottom) return i;
            if (trigger >= top) active = i;
        }
        return active;
    };

    const update = () => {
        updateScrolledClass();
        const idx = findActiveIndex();
        setActive(items[idx]?.el, { scrollNav: true });
    };

    const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            update();
        });
    };

    const onResize = () => {
        sectionTopsDirty = true;
        update();
    };

    const initial = nav.querySelector(`${itemSelector}.${activeClass}`) || items[0].el;
    setActive(initial, { scrollNav: true });

    sectionTopsDirty = true;
    update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
    mql.addEventListener?.('change', onResize);

    requestAnimationFrame(() => {
        sectionTopsDirty = true;
        update();
    });

    return () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        mql.removeEventListener?.('change', onResize);
    };
}